import React, { ReactNode, useCallback, useEffect, useState } from "react";
import "./App.scss";
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import Header from "./components/header/Header";
import { Footer } from "./components/footer/footer";
import SearchResultPage from "./pages/search-page/searchPage";
import RoomBooking from "./pages/room-booking/RoomBooking";
import InfoBanner from "./components/info-banner/InfoBanner";
import ServiceRequestPage from "./pages/service-request/ServiceRequest";
import CustomRedirect from "./components/custom-redirect/CustomRedirect";
import TilePage from "./pages/tile-page/TilePage";
import AboutUs from "./pages/about-us/AboutUs";
import LoginPage from "./pages/login/LoginPage";
import { HttpServicePostLiveAgentChat } from "./services/httpService";
import { AdminPage } from "./pages/admin-page/admin.page";
import { useUniqueUsers } from "./components/unique-users/unique.users";
import { initializeQualtrics } from "./util/qualtrics";
import { CSSVariables, useTheme } from "@me8eon/themes";
import { PwaInstaller } from "@me8eon/pwa_installer";
import { usePwaFlag } from "./hooks/usePwaFlag";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { PublicClientApplication } from "@azure/msal-browser";
import { AnalyticsTracker } from "@me8eon/analytics_tracker";
import { Sidebar } from "@me8eon/sidebar_layout";
import { AISession, RecentChat } from "@me8eon/sessions_messages";
import { DevMode } from "@me8eon/devmode";
import { useTranslation } from "@me8eon/translation";
import useSearchStore from "./store/searchStore";
import { useLanguage } from "@me8eon/language";
import { IntentDevMode } from "@me8eon/react_intent";
import { Communicate } from "./pages/communicate";
import { useFeatureFlag } from "@me8eon/featureflags";
import { metaHumanFFName } from "./pages/communicate/audio.feature.flag";
import { EnvironmentConfig } from "@me8eon/config";
import AiSharePage from "./modules/AiAssist/AiShare.page";
import OnboardingPortal from './pages/onboarding-portal/OnboardingPortal';
import { ROLE_ONBOARDING_PORTAL_USER, ROLE_ONBOARDING_PORTAL_REVIEWER } from './pages/onboarding-portal/OnboardingPortal.constants';
import { ROLE_PRODUCT_TEAM_DASHBOARD } from './pages/product-owner-dashboard/ProductOwnerDashboard.constants';
import ProductOwnerDashboard from './pages/product-owner-dashboard/ProductOwnerDashboard';
import { AiPage } from "./pages/ai/AiPage";
import { rememberSharedLinkForLogin } from "./pages/ai/sharedLinkLogin";
import { listConversations } from "@me8eon/new_ai_utils/src/api/conversations";
import { CONVERSATION_UPDATED_EVENT, defaultChatConfig } from "@me8eon/new_ai_utils/src/config/chat.config";
import { useAuthenticatedFetch } from "@me8eon/new_ai_utils/src/hooks/useAuthenticatedFetch";
import { AidwopsDashboard } from './pages/aidwops/AidwopsDashboard';
import { ROLE_AIDWOPS_DASHBOARD } from "./pages/aidwops/expert-dashboard/ExpertDashboard.constants";

import { ChatProvider } from "@me8eon/new_ai_utils/src/contexts/ChatProvider";
import { NexthinkActionTrackerProvider } from "@me8eon/new_ai_utils/src/contexts/NexthinkActionTrackerProvider";
import { RightSidebar } from "@me8eon/new-ai/src/components/RightSidebar/RightSidebar";

function AuthenticatedJourney({
    children,
    isRightSidebarOpen,
    onRightSidebarOpenChange,
}: {
    children: ReactNode;
    isRightSidebarOpen: boolean;
    onRightSidebarOpenChange: (open: boolean) => void;
}) {
    const { loggedIn, localAccountId } = useUserData();
    return loggedIn ? (
        <JourneyWithActivities
            key={localAccountId}
            isRightSidebarOpen={isRightSidebarOpen}
            onRightSidebarOpenChange={onRightSidebarOpenChange}
        >
            {children}
        </JourneyWithActivities>
    ) : (
        <>{children}</>
    );
}

function JourneyWithActivities({
    children,
    isRightSidebarOpen,
    onRightSidebarOpenChange,
}: {
    children: ReactNode;
    isRightSidebarOpen: boolean;
    onRightSidebarOpenChange: (open: boolean) => void;
}) {
    const authenticatedFetch = useAuthenticatedFetch();
    const { featureFlagEnabled } = useUserData();
    const [hasActivity, setHasActivity] = useState(false);
    const showRightSidebar = featureFlagEnabled && hasActivity;

    return (
        <ChatProvider authenticatedFetch={authenticatedFetch} config={defaultChatConfig}>
            <NexthinkActionTrackerProvider>
                <div className={`journey-with-activities ${showRightSidebar ? "journey-with-activities--enabled" : ""} ${showRightSidebar && isRightSidebarOpen ? "journey-with-activities--right-open" : ""}`}>
                    {children}
                </div>
                {featureFlagEnabled && <RightSidebar
                    onActivityChange={setHasActivity}
                    open={isRightSidebarOpen}
                    onOpenChange={onRightSidebarOpenChange}
                />}
            </NexthinkActionTrackerProvider>
        </ChatProvider>
    );
}

function ProtectedRoutes() {
    const userData = useUserData();
    const location = useLocation();

    const loggedIn = userData.loggedIn;
    if (!loggedIn) rememberSharedLinkForLogin(location.pathname, location.hash);
    if (loggedIn && userData.featureFlagEnabled && location.pathname === "/ai") {
        return <Navigate to="/new-ai" replace />;
    }
    return loggedIn ? <Outlet /> : <Navigate to="/" replace state={{ from: location }} />;
}

const NEW_CHAT_LIMIT = 20;
const NEW_CHAT_FALLBACK_TITLE = "New chat";

/**
 * The app shell keeps one sidebar and merges legacy sessions with new-AI
 * conversations. Each row retains its own `/ai` or `/new-ai/:id` behavior.
 */
function GlobalSidebarContent() {
    const authenticatedFetch = useAuthenticatedFetch();
    const { loggedIn, featureFlagEnabled } = useUserData();
    const [newAiSessions, setNewAiSessions] = useState<AISession[]>([]);

    const loadNewAiSessions = useCallback(async () => {
        if (!loggedIn || !featureFlagEnabled) {
            setNewAiSessions([]);
            return;
        }

        const payload = await listConversations(
            authenticatedFetch,
            defaultChatConfig.conversationsEndpoint,
            { limit: NEW_CHAT_LIMIT },
        );
        setNewAiSessions(payload.conversations.map((conversation) => ({
            id: `new:${conversation.id}`,
            threadId: conversation.id,
            source: "new",
            name: conversation.title?.trim() || NEW_CHAT_FALLBACK_TITLE,
            time: conversation.lastActivityAt,
        })));
    }, [authenticatedFetch, loggedIn, featureFlagEnabled]);

    useEffect(() => {
        void loadNewAiSessions().catch((error) => {
            console.warn("Could not load new-AI recent chats; legacy chats remain available.", error);
            setNewAiSessions([]);
        });
    }, [loadNewAiSessions]);

    useEffect(() => {
        const refresh = () => {
            void loadNewAiSessions().catch((error) => {
                console.warn("Could not refresh new-AI recent chats; legacy chats remain available.", error);
                setNewAiSessions([]);
            });
        };
        window.addEventListener(CONVERSATION_UPDATED_EVENT, refresh);
        return () => window.removeEventListener(CONVERSATION_UPDATED_EVENT, refresh);
    }, [loadNewAiSessions]);

    return (
        <div className="recentChat_wrapper">
            <RecentChat externalSessions={newAiSessions} />
        </div>
    );
}

// interface EonUiSectionWrapperProps {
//     children: ReactNode;
//     removeTopSpacing?: boolean;
//     removeBottomSpacing?: boolean;
//     fullWidth?: boolean;
// }


type AppProps = {
    msalInstance: PublicClientApplication;
}

export function App({ msalInstance }: AppProps) {
    const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(() => window.innerWidth >= 1024);
    const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);

    const handleLeftSidebarOpenChange = useCallback((open: boolean) => {
        setIsLeftSidebarOpen(open);
        if (open) {
            setIsRightSidebarOpen(false);
        }
    }, []);

    const handleRightSidebarOpenChange = useCallback((open: boolean) => {
        setIsRightSidebarOpen(open);
        if (open) {
            setIsLeftSidebarOpen(false);
        }
    }, []);

    const handleToggleLeftSidebar = useCallback(() => {
        setIsLeftSidebarOpen((prev) => {
            const next = !prev;
            if (next) {
                setIsRightSidebarOpen(false);
            }
            return next;
        });
    }, []);
    const theme = useTheme();
    const orgName = theme.value;

    const uniqueUsers = useUniqueUsers();
    const showPwa = usePwaFlag();
    const metaHumanFF = useFeatureFlag(metaHumanFFName);
    const userData = useUserData();
    const idToken = useJwtToken();
    const { loggedIn, localAccountId, actualRoles, featureFlagEnabled } = useUserData();
    const [, setLanguage] = useLanguage();
    const { i18n } = useTranslation();

    useEffect(() => {
        if (userData.loggedIn) {
            idToken()//.then(token => fetchAndStoreDLSApiKey(userData.email, token)) //done elsewhere now
                .then(() =>
                    uniqueUsers(userData.email, { ctry: userData.country, CompanyName: userData.company, role: userData.currentRole }));
            HttpServicePostLiveAgentChat();
            initializeQualtrics();
        }
    }, [userData]);

    const {
        fetchUserPref,
        setUserId,
    } = useSearchStore();

    useEffect(() => {
        const fetchData = async () => {
            if (loggedIn) {
                setUserId(localAccountId);
                await fetchUserPref(idToken, localAccountId);
                const lang: string | null = localStorage.getItem("i18nextLng");
                if (i18n.language !== lang && lang) {
                    i18n.changeLanguage(lang);
                    setLanguage(lang);

                }
            }
        };
        fetchData();
    }, [localAccountId, loggedIn]);

    const globalCSSvariables: CSSVariables = {
        ...theme.dataLayout.colorVariables,
    };

    // const EonUiSectionWrapper: React.FC<EonUiSectionWrapperProps> = ({ children, ...props }) => {
    //     return !loggedIn ? children : <EonUiSection {...props}>{children}</EonUiSection>;
    // };

    return (
        <>
            <Router>
                <AnalyticsTracker />
                <div className={`wrapper org-scheme org-scheme-${orgName}`} style={globalCSSvariables}>
                    <Header orgName={orgName} msalInstance={msalInstance} sidebarOpen={isLeftSidebarOpen} onToggleSidebar={handleToggleLeftSidebar} />
                    {showPwa && <PwaInstaller />}
                    <AuthenticatedJourney
                        isRightSidebarOpen={isRightSidebarOpen}
                        onRightSidebarOpenChange={handleRightSidebarOpenChange}
                    >
                        <Sidebar
                            sidebar={<GlobalSidebarContent />}
                            open={isLeftSidebarOpen}
                            onOpenChange={handleLeftSidebarOpenChange}
                        >
                            <div className="content">
                                <DevMode />
                                {/* <EonUiSectionWrapper removeTopSpacing={true}> */}
                                <Routes>
                                    <Route path="/" element={<LoginPage />} />
                                    <Route element={<ProtectedRoutes />}>
                                        <Route
                                            path="/tile"
                                            element={<TilePage />}
                                        />
                                        <Route
                                            path="/ai"
                                            element={featureFlagEnabled ? <Navigate to="/new-ai" replace /> : <AiSharePage />}
                                        />
                                        <Route
                                            path="/vocal-ai"
                                            element={<AiSharePage />}
                                        />
                                        <Route
                                            path="/new-ai"
                                            element={<AiPage />}
                                        />
                                        <Route
                                            path="/new-ai/shared"
                                            element={<AiPage shared />}
                                        />
                                        <Route
                                            path="/new-ai/:threadId"
                                            element={<AiPage />}
                                        />
                                        {metaHumanFF && !EnvironmentConfig?.isHigherEnv && (
                                            <Route
                                                path="/voice-assist"
                                                element={<Communicate />}
                                            />
                                        )}
                                        <Route path="/intent"
                                            element={<IntentDevMode />} />
                                        <Route
                                            path="/tile/share"
                                            element={<AiSharePage />}
                                        />
                                        <Route
                                            path="/admin"
                                            element={<AdminPage />}
                                        />
                                        <Route
                                            path="/search-result"
                                            element={<SearchResultPage />}
                                        />
                                        <Route
                                            path="/roombooking"
                                            element={<RoomBooking />}
                                        />
                                        <Route
                                            path="/my-approvals"
                                            element={
                                                <InfoBanner
                                                    multiLangHeading="underDevelopmentMsg"
                                                    icon="alert"
                                                />
                                            }
                                        />
                                        <Route
                                            path="/orders"
                                            element={
                                                <ServiceRequestPage type="orders" />
                                            }
                                        />
                                        <Route
                                            path="/incidents"
                                            element={
                                                <ServiceRequestPage type="incidents" />
                                            }
                                        />
                                        <Route
                                            path="/it-resources"
                                            element={
                                                <ServiceRequestPage type="itResources" />
                                            }
                                        />
                                        <Route
                                            path="/about-myhelp"
                                            element={<AboutUs />}
                                        />
                                        {(actualRoles.includes(ROLE_ONBOARDING_PORTAL_USER) || actualRoles.includes(ROLE_ONBOARDING_PORTAL_REVIEWER)) && <Route
                                            path="/onboarding-portal"
                                            element={<OnboardingPortal />}
                                        />}
                                        {(actualRoles.includes(ROLE_PRODUCT_TEAM_DASHBOARD)) && <Route
                                            path="/analytics"
                                            element={<ProductOwnerDashboard />}
                                        />}
                                        {actualRoles.includes(ROLE_AIDWOPS_DASHBOARD) && <Route
                                            path="/aidwops"
                                            element={<AidwopsDashboard />}
                                        />}
                                    </Route>
                                    <Route
                                        path="/api/gingco/code"
                                        element={
                                            <CustomRedirect page="roombooking" />
                                        }
                                    />
                                    <Route
                                        path="/authorize/sapias"
                                        element={<CustomRedirect page="tile" />}
                                    />
                                    <Route path="*" element={<Navigate to="/" />} />
                                </Routes>
                                {/* </EonUiSectionWrapper> */}
                            </div>
                        </Sidebar>
                    </AuthenticatedJourney>
                </div>
                <div className={`org-scheme org-scheme-${orgName}`}>
                    <Footer />
                </div>
            </Router>
        </>
    );
}
